import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Fade, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Form_NewFormSubmissionFormDocument,
  Form_NewFormSubmissionFormSubscription,
  Form_NewFormSubmissionProgressDocument,
  Form_NewFormSubmissionProgressSubscription,
  SubscriptionForm_NewFormSubmissionFormArgs,
  SubscriptionForm_NewFormSubmissionProgressArgs,
  useForm_GetFormProgressQuery,
  useForm_GetNextFormQuery,
} from 'queries';

import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import IntakeFormModal from './intake-form-modal';
import { TrustateLogo } from './logos';

const useStyles = makeStyles(() => ({
  welcomeHeader: {
    fontFamily: 'Soleil',
  },
  layout: {
    position: 'relative',
    textAlign: 'center',
    marginTop: '3%',
  },
  welcomeText: {
    fontFamily: 'Soleil',
    size: '16px',
    lineHeight: '20.32px',
    width: '50%',
    marginLeft: '25%',
    marginBottom: '60px',
  },
  subText: {
    fontFamily: 'Soleil',
    fontStyle: 'normal',
    fontWeight: 300,
    size: '14px',
    lineHeight: '17.78px',
    width: '50%',
    marginLeft: '25%',
    marginBottom: '60px',
    textAlign: 'center',
  },
  textAlign: {
    textAlign: 'center',
  },
}));

const IntakeForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();

  const {
    data: nextFormData,
    loading: nextFormLoading,
    subscribeToMore: nextFormMore,
  } = useForm_GetNextFormQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      portalId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting next form: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const {
    data: formProgressData,
    loading: formProgressLoading,
    subscribeToMore,
  } = useForm_GetFormProgressQuery({
    variables: {
      portalId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting form progress: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  useEffect(() => {
    return subscribeToMore<
      Form_NewFormSubmissionProgressSubscription,
      SubscriptionForm_NewFormSubmissionProgressArgs
    >({
      document: Form_NewFormSubmissionProgressDocument,
      variables: { portalId },
      updateQuery: (prevState, { subscriptionData }) => {
        if (!subscriptionData.data) {
          return prevState;
        }

        const { data: newFormSubmissionProgress } = subscriptionData.data;

        return { ...prevState, formProgress: newFormSubmissionProgress };
      },
    });
  }, [subscribeToMore, portalId]);

  useEffect(() => {
    return nextFormMore<
      Form_NewFormSubmissionFormSubscription,
      SubscriptionForm_NewFormSubmissionFormArgs
    >({
      document: Form_NewFormSubmissionFormDocument,
      variables: { portalId },
      updateQuery: (prevState, { subscriptionData }) => {
        if (!subscriptionData.data) {
          return prevState;
        }

        const { data: newFormSubmissionForm } = subscriptionData.data;

        return { ...prevState, nextForm: newFormSubmissionForm };
      },
    });
  }, [nextFormMore, portalId]);

  return (
    <Fade in={true}>
      <Grid container spacing={3}>
        <Grid item xs={12} className={classes.layout}>
          <TrustateLogo />
          {nextFormData && formProgressData && !nextFormLoading && (
            <IntakeFormModal
              onActionClick={() => history.push(`dashboard`)}
              onExitClick={() => history.push(`dashboard`)}
              showModal
              setShowModal={() => true}
              loading={nextFormLoading || formProgressLoading}
              startingForm={nextFormData.data.formId}
              progress={formProgressData.data}
            />
          )}
        </Grid>
      </Grid>
    </Fade>
  );
};

export default IntakeForm;
